import React from 'react';
import PropTypes from "prop-types";
import {useDispatch} from "react-redux";
import {Button, Col, Container, OverlayTrigger, Row, Tooltip} from 'react-bootstrap';
import {isEmpty} from "lodash";
import {CollapsibleCard} from './CollapsibleCard';
import {labStatusMap, StatusLabel} from '../constants';
import {formatLocalDate} from '../../../../utils/util';
import {getFileDownload} from "../../../../actions/fileUploadActions";
import {adtTypeMap, AdtTypeStatusStyle, HospitalEventStatusStyle,} from "../../HospitalEvents/constants";
import {formatDateToShortMDY} from "../../HospitalEvents/utils";

const componentName = 'EventTimelineCard';

const EventTimelineCard = ({eventTimeline, idx, toggleIndividual, open}) => {
  const dispatch = useDispatch();

  const isHospitalization = eventTimeline.type === 'Hospitalization';

  const getTitle = () => {
    return eventTimeline.typeDisplayValue;
  }

  const getSubTitle = () => {
    switch (eventTimeline.type) {
      case "Appointment":
        return "Appointment Date: " + formatLocalDate(
          eventTimeline.effectiveDate)
      case "Assessment":
        return "Assessment Date: " + formatLocalDate(
          eventTimeline.effectiveDate)
      case "Lab Data":
        return eventTimeline.result;
      case "Care Opportunity":
        return eventTimeline.displayNameAbbrev;
      default:
        return eventTimeline.generatedBy;
    }
  }

  const getStatus = () => {
    if (eventTimeline.type === "Lab Data") {
      return labStatusMap[eventTimeline.status] || eventTimeline.status;
    }
    return eventTimeline.status;
  };

  const handleClickOnLink = (event, fileId, fileName) => {
    event.preventDefault();
    dispatch(getFileDownload(fileId, fileName));
  };

  const tabElements = {
    Appointment: 'appointments',
    Assessment: 'assessments',
    ReferralNotes: 'referrals',
    CareProgramActivity: 'careprograms',
    CareProgram: 'careprograms',
    Hospitalization: 'hospitalEvents'
  }

  const disableRedirect = (event) => {
    const element = document.getElementById(
      'patientDetailsTabs-tab-' + tabElements[event.replaceAll(/\s/g, "")]);
    return element !== undefined && element !== null
  }

  const handleRedirect = (event, id, referenceId) => {
    document.getElementById(
      'patientDetailsTabs-tab-' + tabElements[event.replaceAll(/\s/g, "")]).click()
    sessionStorage.setItem('itemID', referenceId ?? id)
    sessionStorage.setItem('redirectTab',
      (tabElements[event.replaceAll(/\s/g, "")]))
  }

  const getHospitalEventLabel = (adtType, admitDate, dischargeDate) => {
    if (adtType === "ADMIT") {
      return formatDateToShortMDY(admitDate);
    }
    if (admitDate && dischargeDate) {
      return `${formatDateToShortMDY(admitDate)} - ${formatDateToShortMDY(
        dischargeDate)}`;
    }
    return formatDateToShortMDY(admitDate) || formatDateToShortMDY(
      dischargeDate);
  }

  const canRedirect = disableRedirect(eventTimeline.type);

  const ClosedContent = (
    <Container fluid>
      <Row className=" card-header">
        <Col md={6}>
          <div className="d-flex align-items-center">
            <Button variant="link"
              id={`${componentName}-ccTitle-${idx}`}
              onClick={() => toggleIndividual()}
              className="text-start text-dark p-0 text-decoration-none d-flex align-items-center"
            >
              <div className="d-flex align-items-center">
                {open
                  ? <i id={`${componentName}-ccMinus-${idx}`}
                      className="fa-solid fa-chevron-down me-4"/>
                  : <i id={`${componentName}-ccPlus-${idx}`}
                      className="fa-solid fa-chevron-right me-4"/>
                }
                <div className="d-inline-block">
                  <div className='d-flex align-items-center'>
                  <span id={`${componentName}-ccTitle-${idx}`}
                        className="fs-5 fw-bold d-block lh-1">{getTitle()}</span>
                  </div>
                  <span id={`${componentName}-ccSubTitle-${idx}`}
                        className="text-muted">{getSubTitle()}</span>
                </div>
              </div>
            </Button>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Go to {eventTimeline.type}</Tooltip>}
            >
              <Button variant="link"
                aria-label={`Go to ${eventTimeline.type}`}
                disabled={!canRedirect}
                className={`p-0 border-0 ms-4 text-decoration-none ${canRedirect ? "text-primary cursor-p" : "text-muted cursor-not-allowed"}`}
                onClick={(event) => {
                  event.stopPropagation();
                  if (canRedirect) {
                    handleRedirect(eventTimeline.type,
                      eventTimeline.id, eventTimeline.referenceId)
                  }
                }}
              >
                <i className="fa-regular fa-arrow-up-right-from-square fs-8" />
              </Button>
            </OverlayTrigger>
          </div>
        </Col>

        <Col md={6}>
          {isHospitalization ? <div
              className="d-flex justify-content-between align-items-center">
              <AdtTypeStatusStyle
                className="badge rounded-pill border-0"
                type={eventTimeline.status}
              >
                {adtTypeMap[eventTimeline.status] || eventTimeline.status}
              </AdtTypeStatusStyle>

              <HospitalEventStatusStyle className="badge rounded-pill border-0">
                {getHospitalEventLabel(eventTimeline?.status,
                  eventTimeline?.startDate, eventTimeline?.endDate)}
              </HospitalEventStatusStyle>
            </div> :
            <div className="d-flex justify-content-end align-items-center">
              <StatusLabel className="badge badge-pill border-0"
                           status={eventTimeline?.status}
                           type={eventTimeline?.type}>
                {getStatus()}
              </StatusLabel>
            </div>
          }
        </Col>


      </Row>
    </Container>
  );

  const OpenContent = (
    <Container className="p-3">
      <Row>
        <Col className="ps-5">
          {eventTimeline?.details?.map((detail, detailIdx) =>
            <Row key={`${detail?.name ?? "detail"}-${detailIdx}`}>
              <Col id={`${componentName}-detailName-${idx}`}
                   className="form-label col-form-label" sm={3}>
                {detail.name}
              </Col>
              <Col className="d-flex flex-column align-items-center gap-2">
                {detail?.values?.map(
                  (value, valueIdx) => <span key={`${detail?.name ?? "value"}-${valueIdx}`} id={`${componentName}-detailValue-${idx}`}>
                  {value ?? ""}</span>)}
              </Col>
            </Row>
          )}

          {!isEmpty(eventTimeline?.files) &&
            <Row>
              <Col id={`${componentName}-infoDDLinkName-${idx}`}
                   className="form-label col-form-label" sm={3}>
                Files
              </Col>
              <Col className="d-flex align-items-start flex-column">
                {eventTimeline?.files?.map((file, fileIdx) => <p key={`${file?.id ?? file?.name ?? "file"}-${fileIdx}`} style={{ marginBottom: 0 }}>
                  <button
                    id={`${componentName}-infoDDLinkValue-${idx}`}
                    type="button"
                    className="btn btn-link p-0 text-decoration-underline"
                    onClick={(event) => {
                      handleClickOnLink(event, file.id, file.name)
                    }}
                  >
                    {file.name ?? ""}
                  </button></p>)}
              </Col>
            </Row>}
        </Col>
      </Row>
    </Container>
  );

  return (
    <CollapsibleCard
      openContent={OpenContent}
      open={open}
      idx={idx}
      closedContent={ClosedContent}
    />
  );
};

EventTimelineCard.propTypes = {
  eventTimeline: PropTypes.object,
  idx: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  toggleIndividual: PropTypes.func,
  open: PropTypes.bool,
};

export default EventTimelineCard
